import { useCallback, useEffect, useState } from 'react';
import { Clock, GraduationCap } from 'lucide-react';
import { cn } from '@/lib/utils';
import api from '@/lib/api';
import { FlowHistoryPanel } from '@/components/creationFlows/FlowHistoryPanel';
import {
  formatTutorialDate,
  isTutorialFlowProject,
  tutorialStatusLabel,
} from '@/lib/learningTutorial/tutorialHistory';

/**
 * @param {{
 *   open: boolean,
 *   onToggle: () => void,
 *   activeProjectId?: string | null,
 *   onSelectProject: (project: object) => void,
 *   onNewTutorial: () => void,
 *   refreshKey?: number,
 *   filterProject?: (project: object) => boolean,
 *   title?: string,
 *   description?: string,
 *   newLabel?: string,
 *   collapsedLabel?: string,
 *   emptyMessage?: string,
 *   testId?: string,
 *   getStatusLabel?: (project: object) => string,
 *   activeClassName?: string,
 *   activeTitleClassName?: string,
 * }} props
 */
export function TutorialHistorySidebar({
  open,
  onToggle,
  activeProjectId = null,
  onSelectProject,
  onNewTutorial,
  refreshKey = 0,
  filterProject = isTutorialFlowProject,
  title = 'Tutorial history',
  description = 'Staged tutorial projects in your library. Click one to reload its pipeline state and form.',
  newLabel = 'New tutorial',
  collapsedLabel = 'Tutorials',
  emptyMessage = 'No tutorials yet. Generate one to see it here.',
  testId = 'tutorial-history-sidebar',
  getStatusLabel = tutorialStatusLabel,
  activeClassName = 'bg-amber-500/15 border-amber-500/40',
  activeTitleClassName = 'text-amber-100',
}) {
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/learning-tutorials/projects', {
        params: { summary: true, limit: 50 },
      });
      const list = (data.projects || []).filter(filterProject);
      setProjects(list);
    } catch {
      setProjects([]);
    } finally {
      setLoading(false);
    }
  }, [filterProject]);

  useEffect(() => {
    if (!open) return;
    load();
  }, [load, refreshKey, open]);

  return (
    <FlowHistoryPanel
      open={open}
      onToggle={onToggle}
      title={title}
      description={description}
      icon={GraduationCap}
      variant="dark"
      loading={loading}
      count={projects.length}
      onRefresh={load}
      refreshLoading={loading}
      onNew={onNewTutorial}
      newLabel={newLabel}
      emptyMessage={emptyMessage}
      collapsedLabel={collapsedLabel}
      testId={testId}
      footer={`${projects.length} tutorial${projects.length === 1 ? '' : 's'}`}
    >
      <ul className="space-y-1">
        {projects.map((p) => {
          const active = p.id === activeProjectId;
          const topic = p.intake?.topic || p.title || 'Untitled';
          const status = getStatusLabel(p);
          return (
            <li key={p.id}>
              <button
                type="button"
                onClick={() => onSelectProject(p)}
                className={cn(
                  'w-full text-left rounded-sm px-2.5 py-2 border transition-colors',
                  active
                    ? activeClassName
                    : 'border-transparent hover:bg-white/5 hover:border-slate-800',
                )}
                data-testid={`${testId}-item-${p.id}`}
              >
                <p
                  className={cn(
                    'text-sm font-medium line-clamp-1',
                    active ? activeTitleClassName : 'text-slate-200',
                  )}
                >
                  {p.title || topic}
                </p>
                <p className="text-[11px] text-slate-500 line-clamp-1 mt-0.5">{topic}</p>
                <div className="flex items-center justify-between gap-2 mt-1.5">
                  <span
                    className={cn(
                      'text-[9px] font-semibold uppercase tracking-wide px-1 py-0.5 rounded-sm border',
                      status === 'Ready'
                        ? 'text-emerald-400 border-emerald-500/30 bg-emerald-500/10'
                        : 'text-slate-500 border-slate-700 bg-slate-900/50',
                    )}
                  >
                    {status}
                  </span>
                  <span className="text-[10px] text-slate-600 flex items-center gap-0.5 shrink-0">
                    <Clock className="w-3 h-3" />
                    {formatTutorialDate(p.updated_at || p.created_at)}
                  </span>
                </div>
              </button>
            </li>
          );
        })}
      </ul>
    </FlowHistoryPanel>
  );
}
